import React, { useState } from 'react';
import { components } from 'react-select';
import cn from 'classnames';

import Checkbox from '@components/common/Checkbox';
import ItemActionsDropdown from '@components/common/Select/components/Option/ItemActionsDropdown';
import ItemActionsIcons from '@components/common/Select/components/Option/ItemActionsIcons';
import { SelectOptionProps, THandleChange } from '@components/common/Select/Select';
import Tooltip from '@components/common/Tooltip';

import { getChangedValues } from '@utils/multiSelect';

import { TSelectOptionType } from '@components/common/Select/Select.types';

import { DELAY_FOR_TOOLTIP, selectAllOption } from '@components/common/Select/constants';

interface IOptionProps {
	deleteTooltip?: string;
	editTooltip?: string;
	handleChange?: THandleChange;
	isEditable: boolean;
	isRenamable?: boolean;
	isGroups?: boolean;
	maxWidth?: number;
	onDeleteClick?: THandleChange;
	onEditClick?: THandleChange;
	onRenameClick?: THandleChange;
	props: SelectOptionProps;
	renameTooltip?: string;
	collapsedGroups?: string[];
	isActionsDropdown?: boolean;
	openActionsDropdownId: string | null;
	handleToggleActionsDropdown: (id: string | null) => void;
}

export const Option = ({
	deleteTooltip = 'Delete preset',
	editTooltip,
	handleChange,
	isEditable = false,
	isRenamable = false,
	isGroups = false,
	maxWidth,
	onDeleteClick,
	onEditClick,
	onRenameClick,
	props,
	renameTooltip,
	collapsedGroups,
	isActionsDropdown = false,
	openActionsDropdownId,
	handleToggleActionsDropdown,
}: IOptionProps) => {
	const [isFocused, setFocused] = useState(false);

	const groupLabel = props.data.groupLabel;
	const parentGroup = props.data.__group;
	const isCollapsed = collapsedGroups?.includes(parentGroup || groupLabel);

	const { options, getValue, data, label, isSelected, isMulti } = props || {};

	const isCheckboxFixed = data?.isFixed;
	const isCheckboxDisabled = data?.disabled;
	const tooltipText = (data as { tooltipText?: string })?.tooltipText;
	const selectedOptions: Array<TSelectOptionType> = getValue() as Array<TSelectOptionType>;
	//Select all part
	const allOptionsWithoutGroup = (options as TSelectOptionType[]).reduce<Array<TSelectOptionType>>((acc, option) => {
		const subOptions =
			option?.options?.filter(
				(opt) => opt?.value !== selectAllOption?.value && !opt?.isPlaceholder,
			);

		if ((option as TSelectOptionType)?.value === selectAllOption?.value) {
			return acc;
		}

		if (subOptions?.length) {
			return [...acc, ...subOptions];
		}

		return option?.isPlaceholder ? acc : [...acc, option];
	}, []);

	const isAllOption = data.value === selectAllOption?.value;
	const isAllOptionSelected = allOptionsWithoutGroup.length === selectedOptions.length && !!selectedOptions.length;
	const isIndefiniteAllOption = !!selectedOptions.length && !isAllOptionSelected;

	const resetFocus = () => setFocused(false);
	const setFocus = () => setFocused(true);
	const handleRename = () => onRenameClick && onRenameClick(data);
	const handleEdit = () => onEditClick && onEditClick(data);
	const handleDelete = () => onDeleteClick && onDeleteClick(data);

	const toggleMultiOption = () => {
		if (isCheckboxFixed || isCheckboxDisabled) return;

		const changedValues = getChangedValues(selectedOptions, [data]);

		if (handleChange) handleChange(changedValues);
	};

	const toggleAllOption = () => {
		const optionsWithoutFixedAndDisabled = allOptionsWithoutGroup.filter(
			(value) => !(value?.isFixed || value?.disabled),
		);
		const checkedDisabledOptions = selectedOptions.filter((option) => option?.disabled);
		const checkedFixedOptions = selectedOptions.filter((option) => option?.isFixed);
		const availableOptions = [...checkedFixedOptions, ...checkedDisabledOptions, ...optionsWithoutFixedAndDisabled];
		const isShouldUncheck = availableOptions.length === selectedOptions.length;

		if (handleChange) {
			if (isShouldUncheck) {
				const staticSelectedOptions = selectedOptions.reduce<Array<TSelectOptionType>>((acc, option) => {
					if (option?.isFixed || option?.disabled) {
						acc = [...acc, option];
					}
					return acc;
				}, []);

				handleChange(staticSelectedOptions);
			} else {
				handleChange(availableOptions);
			}
		}
	};

	const isPlaceholder = props?.data?.isPlaceholder;

	if (isPlaceholder) {
		return isGroups && isCollapsed
			? null
			: (
			<components.Option
				{...props}
				isDisabled
			>
				<div className='f5gSelectEmptyGroupOption'>{label}</div>
			</components.Option>
		);
	}

	return isGroups && isCollapsed
		? null
		: (
			<Tooltip
				content={tooltipText || label}
				delay={[DELAY_FOR_TOOLTIP, null]}
				subContent={data?.subText}
			>
				<div
					className='f5gEditableSelectEditButtonsContainer'
					onMouseEnter={setFocus}
					onMouseLeave={resetFocus}
				>
					<components.Option {...props}>
						{isMulti ? (
							isAllOption ? (
								<div
									data-testid={`checkbox-${label}`}
									onClick={toggleAllOption}
								>
									<Checkbox
										checked={isAllOptionSelected}
										isIndefinite={isIndefiniteAllOption}
										isLabelTooltipDisabled
										isOverflow={!!maxWidth}
										isPropagation
										label={label}
										maxWidthOverflow={maxWidth}
										name={label}
									/>
								</div>
							) : (
								<div
									data-testid={`checkbox-${label}`}
									onClick={toggleMultiOption}
								>
									<Checkbox
										checked={isSelected}
										disabled={isCheckboxFixed || isCheckboxDisabled}
										isLabelTooltipDisabled
										isOverflow={!!maxWidth}
										isPropagation
										label={label}
										lineHeight={20}
										maxWidthOverflow={maxWidth}
										name={label}
									/>
								</div>
							)
						) : (
							label
						)}
						{data?.subText &&
							<div className={cn('f5gEditableSelectEditButtonsSubText', { 'selectedOption': isSelected })}>
								{data.subText}
							</div>}
					</components.Option>
					{isActionsDropdown
						? <ItemActionsDropdown
							data={data}
							handleDelete={handleDelete}
							handleEdit={handleEdit}
							handleRename={handleRename}
							handleToggleActionsDropdown={handleToggleActionsDropdown}
							isActionsDropdownOpen={openActionsDropdownId === data.id}
							isEditable={isEditable}
							isRenamable={isRenamable}
							isSelected={isSelected}
							onDeleteClick={onDeleteClick}
							onEditClick={onEditClick}
							onRenameClick={onRenameClick}
						/>
						: <ItemActionsIcons
							data={data}
							deleteTooltip={deleteTooltip}
							editTooltip={editTooltip}
							handleDelete={handleDelete}
							handleEdit={handleEdit}
							handleRename={handleRename}
							isEditable={isEditable}
							isFocused={isFocused}
							isRenamable={isRenamable}
							isSelected={isSelected}
							onDeleteClick={onDeleteClick}
							onEditClick={onEditClick}
							onRenameClick={onRenameClick}
							renameTooltip={renameTooltip}
							resetFocus={resetFocus}
						/>
					}
				</div>
			</Tooltip>
		);
};
